import React, { Dispatch, SetStateAction, useMemo } from "react";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";
import { StepReducedAction } from "../utils";
import TextChip from "../../../../../../../design-system-components/TextChip/TextChip";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { useParams } from "react-router-dom";
import { formatClassAcronym } from "../../../../utils";
import { Group } from "../../../../../../../interfaces/Session";
import { DuoConstruction } from "../AddDuoModal";
import useTeacherDuo from "../../../useTeacherDuo";

import "./ChooseGroup.scss";
import AccessibleButton from "../../../../../../../components/AccessibleButton/AccessibleButton";

type Props = {
    setDuo: Dispatch<SetStateAction<DuoConstruction>>;
    setStep: Dispatch<StepReducedAction>;
};

const ChooseGroup = ({ setDuo, setStep }: Props) => {
    const intl = useIntl();
    const { resourceId } = useParams<{ resourceId: string }>();
    const { getGroups } = useTeacherDuo();

    const groups = useMemo(
        () =>
            getGroups(resourceId).filter(
                (group) => group.students && group.students.length >= 2
            ),
        [resourceId, getGroups]
    );

    const handleSelectGroup = (group: Group) => {
        setDuo((duo) => {
            if ((duo.group && duo.group.id !== group.id) || !duo.group)
                return {
                    group,
                };
            return duo;
        });
        setStep({ type: "nextStep" });
    };

    return (
        <div className="choose-group-container">
            <h4>{intl.formatMessage(messages.myClasses)}</h4>
            <div>
                {groups.map((group) => (
                    <AccessibleButton
                        key={group.id}
                        className={cn("choose-group__group-container", {
                            "--locked": group.locked,
                        })}
                        onPress={() => {
                            handleSelectGroup(group);
                        }}
                        isDisabled={group.locked}
                    >
                        <TextChip
                            classNames="choose-group__acronym"
                            content={formatClassAcronym(group.name)}
                            defaultColor={false}
                        />
                        <div className="choose-group__text-container">
                            <span>{group.name}</span>
                            {group.locked && (
                                <span className="locked-text">
                                    {intl.formatMessage(
                                        messages.resourceLockedForThisGroup
                                    )}
                                </span>
                            )}
                        </div>
                        {group.locked ? (
                            <Icon
                                className="choose-group__locked-icon"
                                path="padlock"
                                color="var(--dark-grey)"
                            />
                        ) : (
                            <Icon
                                path="chevron_right"
                                size="large"
                                color="var(--text-main-default)"
                            />
                        )}
                    </AccessibleButton>
                ))}
            </div>
        </div>
    );
};
export default ChooseGroup;

const messages = defineMessages({
    myClasses: {
        id: "duoMode-addDuoModal-myClasses",
        defaultMessage: "My classes",
    },
    resourceLockedForThisGroup: {
        id: "duoMode-addDuoModal-resourceLockedForThisGroup",
        defaultMessage: "Module locked for this class",
    },
});
